import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, forwardRef, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Client } from '@df/core/service/client';
import { AdvancedCommerceSearchOutletComponentDef, AdvancedCommerceStore } from '@df/module-advanced-commerce';
import { ImageModule } from '@df/module-image/image.module';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuTrackClickDirective } from '@df/module-klevu-search/klevu-track-click.directive';
import { Search } from '@df/search/search.interfaces';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { NListing } from 'projects/app-default/src/app/catalog/ui/listing/product-listing.interface';
import { ProductComponent } from 'projects/app-default/src/app/catalog/ui/product/product.component';
import { SearchBannerComponent } from 'projects/app-default/src/app/search/ui/banner/search-banner.component';
import { IPopularSearchesItem } from 'projects/app-default/src/app/search/ui/outlet/search-outlet.component';
import { GtmImpressionTrackDirective } from '../../../gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '../../../gtm/gtm-track-product-click.directive';
import { VipService } from '../../../vip/vip.service';
import { AdvancedCommerceSearchOutletNoResultsComponent } from './no-results/advanced-commerce-search-outlet-no-results.component';

@Component({
    selector: 'advanced-commerce-search-outlet',
    templateUrl: './../../../../search/ui/outlet/search-outlet.component.html',
    styleUrls: ['./../../../../search/ui/outlet/search-outlet.component.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        ImageModule,
        KlevuTrackClickDirective,
        NgClass,
        NgComponentOutlet,
        forwardRef(() => ProductComponent),
        RouterLink,
        SearchBannerComponent,
        SizeClassDirective,
        ToggleModule,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceSearchOutletComponent extends AdvancedCommerceSearchOutletComponentDef {
    titleViewAll = input<string>();
    readonly client = inject(Client);
    readonly productResource = inject(ProductResource);
    protected afterContentInit = inject(AfterContentInitDirectiveService);
    protected vipService = inject(VipService);

    readonly listId = 'search';
    readonly listName = 'Search';

    heading = computed(() => {
        const headings = this.store.searchHeadings();

        return this.store.hasTerm() ? headings['products'] : headings['home_products'];
    });

    items = computed(() => {
        return this.store.itemsEntities();
    });

    protected productsLoadedSignal = signal(0);

    protected loadProductsEffect = effect(() => {
        const items = this.items();
        this.productsLoadedSignal.set(0);

        const productIds = items?.filter(item => item.type === 'product').map(item => item.id) || [];

        if (productIds.length > 0) {
            this.productResource.loadAllIds(productIds, 2).then(async products => {
                await this.vipService.loadForProducts(products);
                this.productsLoadedSignal.set(1);
            });
        } else {
            this.productsLoadedSignal.set(1);
        }
    });

    get shouldShow(): boolean {
        return (this.term?.length || 0) > 2 && this.products?.length > 0;
    }

    get useKlevu() {
        return false;
    }

    get hasNonProductResults(): boolean {
        return (this.store.response()?.elements?.['autocomplete']?.['popular_searches']?.results?.length || 0) > 0;
    }

    get popularSearchesLabel(): string {
        return this.store.searchPopularSearches().label || 'Suggested Results';
    }

    get popularSearches(): Search.ITerm[] | undefined {
        return this.store.searchPopularSearches().items?.map(barePhrase => ({ phrase: this.boldTerm(barePhrase), barePhrase }));
    }

    get suggestedCategoriesLabel(): string {
        return this.store.searchCategories().label || 'Suggested Categories';
    }

    get suggestedCategories(): IPopularSearchesItem[] | undefined {
        return this.store.searchCategories().items?.map(i => ({ name: this.boldTerm(i.text), link: i.link })) || [];
    }

    get suggestedPagesLabel(): string {
        return '';
    }

    get suggestedPages(): IPopularSearchesItem[] | undefined {
        return undefined;
    }

    boldTerm(text: string): string {
        if (this.store.searchRequest()?.q) {
            // Retrieve the search term and split it into individual words
            const term = `${this.store.searchRequest()?.q}`;
            const words = term.split(/\s+/); // Split by any whitespace

            // Create a regular expression to match any of the words
            const regex = new RegExp(`(${words.join('|')})`, 'gi'); // 'g' for global, 'i' for case insensitive

            // Replace each match with a bold version of it
            return text.replace(regex, match => '<b>' + match + '</b>');
        }

        return text;
    }

    get terms() {
        return this.store.searchNoResults().items;
    }

    get products() {
        if (!this.term?.length || this.productsLoadedSignal() === 0) {
            return [];
        }

        return (this.items() || [])
            .filter(item => {
                if (item.type !== 'product') return true;
                const product = this.productResource.getExisting(item.id);
                return product?.data !== undefined;
            })
            .slice(0, 4);
    }

    get term() {
        return this.store.searchRequest()?.q;
    }

    get noResultsOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceSearchOutletNoResultsComponent };
    }

    get clickCode(): string | undefined {
        return this.store.metaClickCode();
    }

    protected productAsAdvancedCommerceItem(product: AdvancedCommerceStore.Items.Item): AdvancedCommerceStore.Items.Item {
        return product;
    }

    protected klevuTrackData(model: AdvancedCommerceStore.Items.Item): KlevuSearch.ITrackSearchProductClickData | undefined {
        return this.useKlevu ? { product: this.productResource.get(model.id), term: this.term } : undefined;
    }
}
